import { INTX_TYPES } from "../../constants/intxConstants";

import { Volume } from "./Intersection";
import { ConventionalSignal } from "./ConventionalSignal/ConventionalSignal";
import {
  ContinuousGreenTNB,
  ContinuousGreenTSB,
  ContinuousGreenTEB,
  ContinuousGreenTWB,
} from "./CGT/ContinuousGreenT";
import { AllWayStopControl } from "./AWSC/AWSC";
import { Bowtie } from "./Bowtie/Bowtie";
import { FullDLT } from "./FDLT/FullDLT";
import { MedianUTurn } from "./MUT/MedianUTurn";
import { PartialDLT } from "./PDLT/PartialDLT";
import { PartialMUT } from "./PMUT/PartialMUT";
import { SplitIntersection } from "./Split/SplitIntersection";
import { CenterTurnOverpass } from "./CTO/CenterTurnOverpass";
import { RCUT } from "./RCUT/RCUT";
import { Echelon } from "./Echelon/Echelon";
import {
  QuadrantRoadwayNE,
  QuadrantRoadwayNW,
  QuadrantRoadwaySE,
  QuadrantRoadwaySW,
} from "./QR/QuadrantRoadway";

import {
  Mini50Roundabout,
  Mini75Roundabout,
} from "./MiniRoundabout/MiniRoundabout";
import { Roundabout } from "./Roundabout/Roundabout";
import { TWSC } from "./TWSC/TWSC";
import { RCUTUnsigIntersection } from "./RCUTunsig/RCUTUnsig";

import { TraditionalDiamond } from "./TraditionalDiamond/TraditionalDiamond";
import { ContraflowLeft } from "./ContraFlowLeft/ContraflowLeft";
import { IntgDLT } from "./DLTI/IntgDLT";
import { DivergingDiamond } from "./DivergingDiamond/DivergingDiamond";
import { SinglePointIntg } from "./SinglePoint/SinglePointIntg";
import { DoubleRoundabout } from "./DoubleRdb/DoubleRoundabout";
import { SingleRoundaboutIntg } from "./SingleRdb/SingleRoundaboutIntg";
import { PartialCloverleafA } from "./PCLA/PartialCloverleafA";
import { PartialCloverleafB } from "./PCLB/PartialCloverleafB";

export class IntxBuilder {
  static createCapacityEngine({
    type,
    name,
    volumes,
    globalParams,
    orientation,
  }) {
    const volumesObject = {};
    for (const [key, value] of Object.entries(volumes)) {
      if (
        value.left !== undefined &&
        value.through !== undefined &&
        value.right !== undefined &&
        value.truckPct !== undefined
      ) {
        volumesObject[key] = new Volume(value);
      }
    }

    if (Object.keys(volumesObject).length === 0) {
      throw new Error("Invalid intersection volumes: " + volumes);
    }

    switch (type) {
      // Signalized Intersections
      case INTX_TYPES.SIGNAL:
        return new ConventionalSignal(name, volumesObject, globalParams);
      case INTX_TYPES.BOWTIE:
        return new Bowtie(name, volumesObject, globalParams, orientation);
      case INTX_TYPES.CGT_N:
        return new ContinuousGreenTSB(name, volumesObject, globalParams);
      case INTX_TYPES.CGT_S:
        return new ContinuousGreenTNB(name, volumesObject, globalParams);
      case INTX_TYPES.CGT_E:
        return new ContinuousGreenTWB(name, volumesObject, globalParams);
      case INTX_TYPES.CGT_W:
        return new ContinuousGreenTEB(name, volumesObject, globalParams);
      case INTX_TYPES.FDLT:
        return new FullDLT(name, volumesObject, globalParams);
      case INTX_TYPES.PDLT:
        return new PartialDLT(name, volumesObject, globalParams, orientation);
      case INTX_TYPES.MUT:
        return new MedianUTurn(name, volumesObject, globalParams, orientation);
      case INTX_TYPES.PMUT:
        return new PartialMUT(name, volumesObject, globalParams, orientation);
      case INTX_TYPES.SPLIT_INTX:
        return new SplitIntersection(
          name,
          volumesObject,
          globalParams,
          orientation
        );
      case INTX_TYPES.CTO:
        return new CenterTurnOverpass(name, volumesObject, globalParams);
      case INTX_TYPES.RCUT_SIGNALIZED:
        return new RCUT(name, volumesObject, globalParams, orientation);
      case INTX_TYPES.ECHELON:
        return new Echelon(name, volumesObject, globalParams, orientation);
      case INTX_TYPES.QR_NE:
        return new QuadrantRoadwayNE(name, volumesObject, globalParams);
      case INTX_TYPES.QR_NW:
        return new QuadrantRoadwayNW(name, volumesObject, globalParams);
      case INTX_TYPES.QR_SE:
        return new QuadrantRoadwaySE(name, volumesObject, globalParams);
      case INTX_TYPES.QR_SW:
        return new QuadrantRoadwaySW(name, volumesObject, globalParams);

      // Unsignalized Intersections
      case INTX_TYPES.MINI_RBT_50:
        return new Mini50Roundabout(name, volumesObject, globalParams);
      case INTX_TYPES.MINI_RBT_75:
        return new Mini75Roundabout(name, volumesObject, globalParams);
      case INTX_TYPES.ROUNDABOUT:
        return new Roundabout(name, volumesObject, globalParams);
      case INTX_TYPES.ALL_WAY_STOP:
        return new AllWayStopControl(name, volumesObject, globalParams);
      case INTX_TYPES.TWSC:
        return new TWSC(name, volumesObject, globalParams, orientation);
      case INTX_TYPES.RCUT_UNSIGNALIZED:
        return new RCUTUnsigIntersection(
          name,
          volumesObject,
          globalParams,
          orientation
        );

      // Interchanges
      case INTX_TYPES.TRAD_DIAMOND:
        return new TraditionalDiamond(
          name,
          volumesObject,
          globalParams,
          orientation
        );
      case INTX_TYPES.CONTRAFLOW_LEFT:
        return new ContraflowLeft(
          name,
          volumesObject,
          globalParams,
          orientation
        );
      case INTX_TYPES.DLT_INTERCHANGE:
        return new IntgDLT(name, volumesObject, globalParams, orientation);
      case INTX_TYPES.DDI:
        return new DivergingDiamond(
          name,
          volumesObject,
          globalParams,
          orientation
        );
      case INTX_TYPES.SINGLE_POINT_DIAMOND:
        return new SinglePointIntg(
          name,
          volumesObject,
          globalParams,
          orientation
        );
      case INTX_TYPES.SINGLE_RBT:
        return new SingleRoundaboutIntg(
          name,
          volumesObject,
          globalParams,
          orientation
        );
      case INTX_TYPES.DOUBLE_RBT:
        return new DoubleRoundabout(
          name,
          volumesObject,
          globalParams,
          orientation
        );
      case INTX_TYPES.PARTIAL_CLOVERLEAF_A:
        return new PartialCloverleafA(
          name,
          volumesObject,
          globalParams,
          orientation
        );
      case INTX_TYPES.PARTIAL_CLOVERLEAF_B:
        return new PartialCloverleafB(
          name,
          volumesObject,
          globalParams,
          orientation
        );

      default:
        return null;
    }
  }

  static getDefaultInputs(type) {
    switch (type) {
      // Signalized Intersections
      case INTX_TYPES.SIGNAL:
        return ConventionalSignal.getZoneDefaultInputs();
      case INTX_TYPES.BOWTIE:
        return Bowtie.getZoneDefaultInputs();
      case INTX_TYPES.CGT_N:
        return ContinuousGreenTSB.getZoneDefaultInputs();
      case INTX_TYPES.CGT_S:
        return ContinuousGreenTNB.getZoneDefaultInputs();
      case INTX_TYPES.CGT_E:
        return ContinuousGreenTWB.getZoneDefaultInputs();
      case INTX_TYPES.CGT_W:
        return ContinuousGreenTEB.getZoneDefaultInputs();
      case INTX_TYPES.FDLT:
        return FullDLT.getZoneDefaultInputs();
      case INTX_TYPES.PDLT:
        return PartialDLT.getZoneDefaultInputs();
      case INTX_TYPES.MUT:
        return MedianUTurn.getZoneDefaultInputs();
      case INTX_TYPES.PMUT:
        return PartialMUT.getZoneDefaultInputs();
      case INTX_TYPES.SPLIT_INTX:
        return SplitIntersection.getZoneDefaultInputs();
      case INTX_TYPES.CTO:
        return CenterTurnOverpass.getZoneDefaultInputs();
      case INTX_TYPES.RCUT_SIGNALIZED:
        return RCUT.getZoneDefaultInputs();
      case INTX_TYPES.ECHELON:
        return Echelon.getZoneDefaultInputs();
      case INTX_TYPES.QR_NE:
        return QuadrantRoadwayNE.getZoneDefaultInputs();
      case INTX_TYPES.QR_NW:
        return QuadrantRoadwayNW.getZoneDefaultInputs();
      case INTX_TYPES.QR_SE:
        return QuadrantRoadwaySE.getZoneDefaultInputs();
      case INTX_TYPES.QR_SW:
        return QuadrantRoadwaySW.getZoneDefaultInputs();

      // Unsignalized Intersections
      case INTX_TYPES.ROUNDABOUT:
        return Roundabout.getZoneDefaultInputs();
      case INTX_TYPES.ALL_WAY_STOP:
        return AllWayStopControl.getZoneDefaultInputs();
      case INTX_TYPES.TWSC:
        return TWSC.getZoneDefaultInputs();
      case INTX_TYPES.RCUT_UNSIGNALIZED:
        return RCUTUnsigIntersection.getZoneDefaultInputs();

      // Interchanges
      case INTX_TYPES.TRAD_DIAMOND:
        return TraditionalDiamond.getZoneDefaultInputs();
      case INTX_TYPES.CONTRAFLOW_LEFT:
        return ContraflowLeft.getZoneDefaultInputs();
      case INTX_TYPES.DLT_INTERCHANGE:
        return IntgDLT.getZoneDefaultInputs();
      case INTX_TYPES.DDI:
        return DivergingDiamond.getZoneDefaultInputs();
      case INTX_TYPES.SINGLE_POINT_DIAMOND:
        return SinglePointIntg.getZoneDefaultInputs();
      case INTX_TYPES.SINGLE_RBT:
        return SingleRoundaboutIntg.getZoneDefaultInputs();
      case INTX_TYPES.DOUBLE_RBT:
        return DoubleRoundabout.getZoneDefaultInputs();
      case INTX_TYPES.PARTIAL_CLOVERLEAF_A:
        return PartialCloverleafA.getZoneDefaultInputs();
      case INTX_TYPES.PARTIAL_CLOVERLEAF_B:
        return PartialCloverleafB.getZoneDefaultInputs();
      default:
        return null;
    }
  }

  static getLaneConfigLabelsMapping({ type, orientation }) {
    switch (type) {
      // Signalized Intersections
      case INTX_TYPES.SIGNAL:
        return ConventionalSignal.getLaneConfigLabelsMapping();
      case INTX_TYPES.BOWTIE:
        return Bowtie.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.CGT_N:
        return ContinuousGreenTSB.getLaneConfigLabelsMapping();
      case INTX_TYPES.CGT_S:
        return ContinuousGreenTNB.getLaneConfigLabelsMapping();
      case INTX_TYPES.CGT_E:
        return ContinuousGreenTWB.getLaneConfigLabelsMapping();
      case INTX_TYPES.CGT_W:
        return ContinuousGreenTEB.getLaneConfigLabelsMapping();
      case INTX_TYPES.FDLT:
        return FullDLT.getLaneConfigLabelsMapping();
      case INTX_TYPES.PDLT:
        return PartialDLT.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.MUT:
        return MedianUTurn.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.PMUT:
        return PartialMUT.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.SPLIT_INTX:
        return SplitIntersection.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.CTO:
        return CenterTurnOverpass.getLaneConfigLabelsMapping();
      case INTX_TYPES.RCUT_SIGNALIZED:
        return RCUT.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.ECHELON:
        return Echelon.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.QR_NE:
        return QuadrantRoadwayNE.getLaneConfigLabelsMapping();
      case INTX_TYPES.QR_NW:
        return QuadrantRoadwayNW.getLaneConfigLabelsMapping();
      case INTX_TYPES.QR_SE:
        return QuadrantRoadwaySE.getLaneConfigLabelsMapping();
      case INTX_TYPES.QR_SW:
        return QuadrantRoadwaySW.getLaneConfigLabelsMapping();

      // Unsignalized Intersections
      case INTX_TYPES.ROUNDABOUT:
        return Roundabout.getLaneConfigLabelsMapping();
      case INTX_TYPES.ALL_WAY_STOP:
        return AllWayStopControl.getLaneConfigLabelsMapping();
      case INTX_TYPES.TWSC:
        return TWSC.getLaneConfigLabelsMapping();
      case INTX_TYPES.RCUT_UNSIGNALIZED:
        return RCUTUnsigIntersection.getLaneConfigLabelsMapping(orientation);

      // Interchanges
      case INTX_TYPES.TRAD_DIAMOND:
        return TraditionalDiamond.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.CONTRAFLOW_LEFT:
        return ContraflowLeft.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.DLT_INTERCHANGE:
        return IntgDLT.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.DDI:
        return DivergingDiamond.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.SINGLE_POINT_DIAMOND:
        return SinglePointIntg.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.SINGLE_RBT:
        return SingleRoundaboutIntg.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.DOUBLE_RBT:
        return DoubleRoundabout.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.PARTIAL_CLOVERLEAF_A:
        return PartialCloverleafA.getLaneConfigLabelsMapping(orientation);
      case INTX_TYPES.PARTIAL_CLOVERLEAF_B:
        return PartialCloverleafB.getLaneConfigLabelsMapping(orientation);

      default:
        return null;
    }
  }
}
